import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { useLocation, withRouter, useHistory } from 'react-router-dom'
import { ReactComponent as UserAvatar } from '../../../../images/icons/user_avatar.svg'
import { MobileBackProvider, useMobileBack } from '../../../../context/MobileBackContext'

import { FormText } from '../../form'
import useIsMobile from '../../../../hooks/useIsMobile'
import Breadcrumbs from '../../../breadcumbs'
import { useLogout } from '../../../../hooks/useLogout'
import { useNavigation } from '../../../../hooks/useNavigation'
import { logout as logoutAction, setPatientDetails } from '../../../../actions'
import { ChevronDown, ChevronLeft, LogOut, Bars } from '../../../../assets/icons'
import { ACCES_BY_ROLE_GROUP, MOBILE_TABS } from '../../../../constants/accesByRolesGroup'
import { canAccessMenuItem } from '../../../../utils/accessControl'
import { matchesNavigationPath } from '../../../../utils/matchesNavigationPath'
import { getTitleFromPathname } from '../../../../utils/getCurrentRoute'
import colors from '../../../../styled/colors'
import {
	BottomNav,
	BottomNavContainer,
	NavButton as MobileNavButton,
	Spacer,
} from '../mobileLayout/styled'
import { NavText, Title } from '../../text'

const MainLayoutInner = ({ children, auth, patient, setPatientDetails, logoutAction }) => {
	const location = useLocation()
	const history = useHistory()
	const isMobile = useIsMobile()
	const [showUserMenu, setShowUserMenu] = useState(false)
	const [activeTab, setActiveTab] = useState('inicio')
	const { customBack } = useMobileBack()
	const handleLogout = useLogout(setPatientDetails, logoutAction)
	const handleNavigation = useNavigation()

	const route = location.pathname
	const currentTitle = getTitleFromPathname(route) || ''
	const withoutHeader = route === '/inicio/ver-mas' || route === '/inicio'

	const handleBack = () => {
		if (customBack) {
			customBack()
			return
		}
		if (history.length > 1) {
			history.goBack()
		} else {
			history.push('/inicio')
		}
	}

	const getRoutesAvailable = () => {
		const userRoles = auth.gruposRoles || []
		let routes = []
		userRoles.forEach(role => {
			const group = Object.keys(ACCES_BY_ROLE_GROUP).find(key => key === role)
			if (group && ACCES_BY_ROLE_GROUP[group]) {
				routes = routes.concat(ACCES_BY_ROLE_GROUP[group])
			}
		})
		return routes
			.filter(item => canAccessMenuItem(item, auth))
			.map(item => ({
				id: item.id,
				label: item.label,
				icon: item.icon,
				path: item.path,
				roles: item.roles
			}))
	}

	const getMobileTabsAvailable = () => {
		const userRoles = auth.gruposRoles || []
		let tabs = []
		userRoles.forEach(role => {
			const group = Object.keys(MOBILE_TABS).find(key => key === role)
			if (group && MOBILE_TABS[group]) {
				tabs = tabs.concat(MOBILE_TABS[group])
			}
		})
		const mobileAccessibleTabs = tabs.filter(item => canAccessMenuItem(item, auth))
		const bottomTabs = mobileAccessibleTabs.filter(item => item.mobileTab !== 'more')
		const hasMoreTab = mobileAccessibleTabs.some(item => item.mobileTab === 'more')
		const finalTabs = hasMoreTab
			? [...bottomTabs, { id: 'mas', label: 'Más', icon: Bars, path: '/inicio/ver-mas' }]
			: bottomTabs
		return finalTabs.map(item => ({
			id: item.id,
			label: item.mobileLabel || item.label,
			icon: item.mobileIcon || item.icon,
			path: item.path,
			relatedPaths: (item.subRoutes || []).map(subRoute => subRoute.path),
			roles: item.roles
		}))
	}

	useEffect(() => {
		const tabs = getMobileTabsAvailable()
		const found = tabs.find(tab =>
			matchesNavigationPath(location.pathname, tab.path, tab.relatedPaths)
		)
		setActiveTab(found ? found.id : '')
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname])

	const isActive = path => matchesNavigationPath(location.pathname, path)

	const handleMobileNavigation = (path, id) => {
		setActiveTab(id)
		history.push(path)
	}

	return (
		<LayoutRoot isMobile={isMobile}>
			{/* Desktop chrome: top header */}
			{!isMobile && (
				<>
					{showUserMenu && <PageOverlay onClick={() => setShowUserMenu(false)} />}
					<Header>
						<Logo>Mi Portal Clínicas</Logo>
						<UserSection>
							<UserButton onClick={() => setShowUserMenu(!showUserMenu)}>
								<UserContainer>
									<Avatar>
										<UserAvatar width={40} height={40} />
									</Avatar>
									<UserInfo>
										<FormText font={18} fw={600} color="#FFFFFF">
											{auth?.nombre ? `${auth.nombre} ${auth?.apellido}` : ''}
										</FormText>
										<FormText font={14} fw={600} color="#BCBABA">
											{patient?.coberturaMedica
												? `Plan ${patient?.coberturaMedica} ${patient?.planLineaCartilla}`
												: ''}
										</FormText>
									</UserInfo>
								</UserContainer>
								<ChevronIconWrapper isOpen={showUserMenu}>
									<ChevronDown width={16} height={16} />
								</ChevronIconWrapper>
							</UserButton>
							{showUserMenu && (
								<UserDropdown>
									<DropdownItem bc="#F2F2F2" noClick={true}>
										<FormText font={20} fw={700} color="#161616" align="left">
											{auth?.nombre ? `${auth.nombre} ${auth?.apellido}` : ''}
										</FormText>
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											setShowUserMenu(false)
											handleNavigation('/inicio/mis-datos')
										}}
									>
										<FormText font={16} fw={400} color="#161616" align="left">
											Mis datos
										</FormText>
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											setShowUserMenu(false)
											handleLogout()
										}}
									>
										<LogOut width={20} height={20} />
										<FormText font={16} fw={400} color="#DA1E28" align="left">
											Cerrar sesión
										</FormText>
									</DropdownItem>
								</UserDropdown>
							)}
						</UserSection>
					</Header>
				</>
			)}

			{/* Mobile chrome: top header */}
			{isMobile && !withoutHeader && (
				<MobileHeader>
					<HeaderButton onClick={handleBack}>
						<ChevronLeft size={15} color={colors.text.primaryText} />
					</HeaderButton>
					<Title size="18px" color="#000000ff">{currentTitle}</Title>
					<Spacer width="0.6" />
				</MobileHeader>
			)}

			{/* Body: sidebar (desktop) + content (always) */}
			<BodyWrapper isMobile={isMobile}>
				{!isMobile && (
					<Sidebar>
						<Nav>
							{getRoutesAvailable().map(item => {
								const IconComponent = item.icon
								return (
									<NavItem
										key={item.id}
										active={isActive(item.path)}
										onClick={() => handleNavigation(item.path)}
									>
										<NavIcon active={isActive(item.path)}>
											<IconComponent
												size={24}
												color={isActive(item.path) ? '#003d82' : '#161616'}
											/>
										</NavIcon>
										<NavLabel>{item.label}</NavLabel>
									</NavItem>
								)
							})}
						</Nav>
					</Sidebar>
				)}

				{/* Content area — children always live here, stable tree position */}
				<MainContent isMobile={isMobile}>
					{!isMobile && <Breadcrumbs />}
					<ContainerChildren isMobile={isMobile} route={route}>{children}</ContainerChildren>
				</MainContent>
			</BodyWrapper>

			{/* Mobile chrome: bottom nav */}
			{isMobile && (
				<BottomNav>
					<BottomNavContainer>
						{getMobileTabsAvailable().map(tab => {
							const Icon = tab.icon
							return (
								<MobileNavButton
									key={tab.id}
									active={activeTab === tab.id}
									onClick={() => handleMobileNavigation(tab.path, tab.id)}
								>
									<Icon
										size={24}
										color={
											activeTab === tab.id
												? colors.brand.selectGreen
												: colors.text.primaryText
										}
									/>
									<NavText>{tab.label}</NavText>
								</MobileNavButton>
							)
						})}
					</BottomNavContainer>
				</BottomNav>
			)}
		</LayoutRoot>
	)
}

const MainLayout = ({ children, auth, patient, setPatientDetails, logoutAction }) => (
	<MobileBackProvider>
		<MainLayoutInner
			auth={auth}
			patient={patient}
			setPatientDetails={setPatientDetails}
			logoutAction={logoutAction}
		>
			{children}
		</MainLayoutInner>
	</MobileBackProvider>
)

function mapStateToProps({ auth, patient }) {
	return { auth, patient }
}

const mapDispatchToProps = {
	setPatientDetails,
	logoutAction
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MainLayout))

const LayoutRoot = styled.div`
	position: relative;
	min-height: 100vh;
	background-color: ${({ isMobile }) => (isMobile ? 'white' : '#f5f5f5')};
	display: flex;
	flex-direction: column;
`

const PageOverlay = styled.button`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.25);
	border: none;
	padding: 0;
	margin: 0;
	z-index: 999;
	cursor: default;
`

const ContainerChildren = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	padding: ${({ isMobile, route }) =>
		isMobile ? (route === '/inicio/ver-mas' ? '0' : '24px 16px') : '0'};
	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		${({ isMobile }) => !isMobile && 'margin: 0 55px;'}
	}
`

const Header = styled.header`
	background-color: ${p => p.theme.colors.brand.blueOmint || '#003d82'};
	color: white;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 24px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	position: sticky;
	top: 0;
	z-index: 1000;
`

const Logo = styled.h1`
	font-size: 20px;
	font-weight: 600;
	margin: 25px;
	color: white;
`

const UserSection = styled.div`
	position: relative;
`

const UserButton = styled.button`
	display: flex;
	align-items: center;
	gap: 32px;
	background: transparent;
	border: none;
	color: white;
	cursor: pointer;
	padding: 8px 12px;
	border-radius: 8px;
	transition: background-color 0.2s;

	&:hover {
		background-color: rgba(255, 255, 255, 0.1);
	}
`

const Avatar = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		width: 40px;
		height: 40px;
	}
`

const UserInfo = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;

	@media (max-width: 768px) {
		display: none;
	}
`

const ChevronIconWrapper = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	transition: transform 0.2s;
	transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

	path {
		width: 24px;
		height: 24px;
		stroke: #ffffff;
	}

	@media (max-width: 768px) {
		display: none;
	}
`

const UserDropdown = styled.div`
	position: absolute;
	top: 100%;
	right: 0;
	margin-top: 8px;
	background: white;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	min-width: 200px;
	overflow: hidden;
	z-index: 1001;
`

const DropdownItem = styled.button`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 10px;
	width: 100%;
	min-width: 319px;
	padding: 24px 16px;
	background: transparent;
	border: none;
	text-align: left;
	cursor: ${({ noClick }) => (noClick ? 'default' : 'pointer')};
	font-size: 14px;
	color: #333;
	transition: background-color 0.2s;
	background-color: ${({ bc }) => bc || 'transparent'};

	&:hover {
		background-color: ${({ bc, noClick }) => (noClick ? bc : '#f5f5f5')};
	}

	&:not(:last-child) {
		border-bottom: 1px solid #e0e0e0;
	}
`

const BodyWrapper = styled.div`
	display: flex;
	flex: 1;
	min-height: ${({ isMobile }) => (isMobile ? '0' : 'calc(100vh - 64px)')};
	flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`

const Sidebar = styled.aside`
	width: 240px;
	background-color: white;
	border-right: 1px solid #e0e0e0;
	@media (max-width: 768px) {
		display: none;
	}
`

const Nav = styled.nav`
	padding: 16px 0;
`

const NavItem = styled.button`
	width: calc(100% - 32px);
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 6px;
	margin: 4px 16px;
	background: ${props => (props.active ? '#EFF5FF' : 'transparent')};
	border: none;
	border-radius: 8px;
	cursor: pointer;
	text-align: left;
	transition: all 0.2s;
	color: ${props => (props.active ? '#003d82' : '#333')};
	font-weight: ${props => (props.active ? '500' : '400')};

	&:hover {
		background-color: ${props => (props.active ? '#e8f4fd' : '#f5f5f5')};
	}
`

const NavIcon = styled.span`
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
`

const NavLabel = styled.span`
	font-size: 14px;
`

const MainContent = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	padding: ${({ isMobile }) => (isMobile ? '0' : '32px')};
	background-color: #ffffff;
	overflow-y: ${({ isMobile }) => (isMobile ? 'auto' : 'visible')};
`

const UserContainer = styled.div`
	display: flex;
	flex-direction: row;
	gap: 12px;
`

const MobileHeader = styled.header`
	background-color: white;
	padding: 1rem;
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: sticky;
	top: 0;
	z-index: 10;
`

const HeaderButton = styled.button`
	padding: 0.5rem;
	margin-left: -0.5rem;
	background-color: transparent;
	border: none;
	border-radius: 0.5rem;
	cursor: pointer;
	transition: background-color 0.2s;

	&:hover {
		background-color: #e5e7eb;
	}
`
